import React, { useState, useCallback, useRef, useEffect } from 'react';

// ── Toast ─────────────────────────────────────────────────────────────────────
let _add = null;
export const useToast = () => ({
  toast: (msg, type='default') => _add?.({ msg, type, id: Date.now() }),
});
export function Toasts() {
  const [list, setList] = useState([]);
  _add = useCallback(t => {
    setList(p => [...p, t]);
    setTimeout(() => setList(p => p.filter(x => x.id !== t.id)), 3500);
  }, []);
  return <div className="toasts">{list.map(t => <div key={t.id} className={`toast toast-${t.type}`}>{t.msg}</div>)}</div>;
}

// ── Spinner ───────────────────────────────────────────────────────────────────
export const Spinner = ({ size=20 }) => <div className="spin" style={{ width:size, height:size }} />;

export const Loading = ({ label='Loading…' }) => (
  <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:280, gap:14, color:'var(--text2)' }}>
    <Spinner size={28} /><span style={{ fontSize:13 }}>{label}</span>
  </div>
);

// ── Badge ─────────────────────────────────────────────────────────────────────
// Status config — short code, display label, badge class
export const STATUS_CONFIG = {
  awaiting_photo:       { short:'AWP',  label:'Awaiting Photo',        cls:'badge-pending'   },
  in_process:           { short:'INP',  label:'In Process',            cls:'badge-inprocess' },
  mqc:                  { short:'MQC',  label:'Quality',              cls:'badge-mqc'       },
  ready_to_model:       { short:'RTM',  label:'Ready to Model',        cls:'badge-ready'     },
  sculpting:            { short:'SCP',  label:'Sculpting',             cls:'badge-ready'     },
  ready_to_print:       { short:'RTP',  label:'Ready to Print',        cls:'badge-ready'     },
  printing:             { short:'PRT',  label:'Printing',              cls:'badge-prod'      },
  finishing:            { short:'FIN',  label:'Finishing',             cls:'badge-finishing' },
  qc:                   { short:'QCI',  label:'QC',                    cls:'badge-qc'        },
  packing:              { short:'PKG',  label:'Packing',               cls:'badge-packing'   },
  shipped_to_solidiphy: { short:'STS',  label:'Shipped to Solidiphy',  cls:'badge-ship'      },
  received_accepted:    { short:'RCVA', label:'Received & Accepted',   cls:'badge-fulfilled' },
  received_rejected:    { short:'RCVR', label:'Received & Rejected',   cls:'badge-cancelled' },
  ready_to_ship:        { short:'RTS',  label:'Ready to Ship',         cls:'badge-ship'      },
  shipped_to_customer:  { short:'STC',  label:'Shipped to Customer',   cls:'badge-fulfilled' },
  cancelled:            { short:'CXL',  label:'Cancelled',             cls:'badge-cancelled' },
  uploaded:             { short:'',     label:'Uploaded',              cls:'badge-ready'     },
};

const STATUS_MAP = Object.fromEntries(
  Object.entries(STATUS_CONFIG).map(([k, v]) => [k, { cls: v.cls, label: v.label }])
);
export const Badge = ({ status }) => {
  const s = STATUS_MAP[status] || { cls:'badge-pending', label: status };
  return <span className={`badge ${s.cls}`}>{s.label}</span>;
};

// ── Rush badge (Phase 9) — shown next to the status badge wherever isRush=true
export const RushBadge = () => <span className="badge badge-rush">🔥 Rush</span>;

// ── Lazy-loaded image (Phase 9) ──────────────────────────────────────────────
// Defers the actual network request (via the OneDrive image proxy) until the
// element is within `rootMargin` of the visible scroll area. IntersectionObserver
// correctly accounts for clipping by scrollable ancestors even with the default
// (viewport) root, so this works inside nested overflow:auto list containers
// without needing an explicit root. Cuts down the burst of simultaneous proxy
// requests that a long list of rows would otherwise fire all at once on mount.
export function LazyImage({ src, alt = '', style, crossOrigin, onClick, rootMargin = '300px', className }) {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!ref.current || visible) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [visible, rootMargin]);

  if (!visible) {
    return <div ref={ref} className={className} style={{ ...style, background:'#f3f4f6' }} onClick={onClick} />;
  }
  return <img ref={ref} src={src} alt={alt} style={style} crossOrigin={crossOrigin} onClick={onClick} className={className} fetchpriority="low" loading="lazy" />;
}

// ── Multi-select status filter dropdown (Phase 9) ────────────────────────────
// options: [{ value, label }], selected: array of values, onChange: (newArray) => void
export function StatusMultiSelect({ options, selected, onChange, placeholder = 'All statuses', width = 200 }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const toggle = (value) => {
    onChange(selected.includes(value) ? selected.filter(v => v !== value) : [...selected, value]);
  };

  const label = selected.length === 0 ? placeholder
    : selected.length === 1 ? (options.find(o => o.value === selected[0])?.label || selected[0])
    : `${selected.length} selected`;

  return (
    <div ref={ref} style={{ position:'relative', width }}>
      <button type="button" className="input" onClick={() => setOpen(o => !o)}
        style={{ width:'100%', fontSize:13, textAlign:'left', display:'flex', justifyContent:'space-between', alignItems:'center', cursor:'pointer', background:'#fff' }}>
        <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{label}</span>
        <span style={{ fontSize:10, color:'#9ca3af', marginLeft:6 }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div style={{ position:'absolute', top:'100%', left:0, marginTop:4, background:'#fff', border:'1px solid #d1d5db',
          borderRadius:8, boxShadow:'0 4px 16px rgba(0,0,0,0.12)', zIndex:100, minWidth:'100%', maxHeight:280, overflowY:'auto', padding:6 }}>
          {selected.length > 0 && (
            <button type="button" onClick={() => onChange([])}
              style={{ fontSize:11, color:'var(--brand)', background:'none', border:'none', cursor:'pointer', padding:'4px 6px', textDecoration:'underline' }}>
              Clear all
            </button>
          )}
          {options.map(opt => (
            <label key={opt.value} style={{ display:'flex', alignItems:'center', gap:8, padding:'5px 6px', fontSize:13, cursor:'pointer', borderRadius:5 }}
              onMouseEnter={e => e.currentTarget.style.background='#f3f4f6'} onMouseLeave={e => e.currentTarget.style.background=''}>
              <input type="checkbox" checked={selected.includes(opt.value)} onChange={() => toggle(opt.value)} />
              {opt.label}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Payment status badge — friendly labels for Stripe's raw enum values ──────
export const PAYMENT_STATUS_CONFIG = {
  not_applicable:     { label:'N/A',                 cls:'badge-neutral'   },
  requires_payment:   { label:'Payment Pending',     cls:'badge-pending'   },
  captured:           { label:'Paid',                cls:'badge-fulfilled' },
  failed:             { label:'Payment Failed',      cls:'badge-cancelled' },
  canceled:           { label:'Canceled',             cls:'badge-neutral'   },
  refunded:           { label:'Refunded',             cls:'badge-cancelled' },
  partially_refunded: { label:'Partially Refunded',  cls:'badge-prod'      },
  waived:             { label:'Waived ($0)',          cls:'badge-fulfilled' }, // Phase 9
};
export const PaymentBadge = ({ status }) => {
  const s = PAYMENT_STATUS_CONFIG[status] || PAYMENT_STATUS_CONFIG.not_applicable;
  return <span className={`badge ${s.cls}`}>{s.label}</span>;
};

// ── Qty control ───────────────────────────────────────────────────────────────
export const Qty = ({ value, onChange, min=1 }) => (
  <div className="qty">
    <button className="qty-btn" type="button" onClick={() => onChange(Math.max(min, value-1))}>−</button>
    <input className="input qty-input" type="number" value={value} min={min}
      onChange={e => onChange(Math.max(min, parseInt(e.target.value)||min))} />
    <button className="qty-btn" type="button" onClick={() => onChange(value+1)}>+</button>
  </div>
);

// ── Order Summary ─────────────────────────────────────────────────────────────
export const OrderSummary = ({ subtotal, shippingCost, taxAmount, taxEnabled, total, shippingTitle, children }) => {
  const f = n => `$${Number(n||0).toFixed(2)}`;
  return (
    <div className="card">
      <div style={{ fontWeight:600, marginBottom:12, fontSize:14 }}>Order summary</div>
      <div className="sum-row"><span>Subtotal</span><span>{f(subtotal)}</span></div>
      <div className="sum-row"><span>Shipping{shippingTitle ? ` (${shippingTitle})` : ''}</span><span>{shippingCost > 0 ? f(shippingCost) : '—'}</span></div>
      {taxEnabled && <div className="sum-row"><span>Tax</span><span>{taxAmount > 0 ? f(taxAmount) : '—'}</span></div>}
      <hr className="divider" />
      <div className="sum-total"><span>Total</span><span>{f(total)}</span></div>
      {children}
    </div>
  );
};

// ── Steps ─────────────────────────────────────────────────────────────────────
export const Steps = ({ steps, current }) => (
  <div className="steps">
    {steps.map((s,i) => (
      <span key={s} className={`step ${i===current?'active':''} ${i<current?'done':''}`}>
        {i < current ? `${s} ✓` : s}
      </span>
    ))}
  </div>
);

// ── Empty ─────────────────────────────────────────────────────────────────────
export const Empty = ({ icon='📭', title, subtitle, action }) => (
  <div className="empty">
    <div className="empty-icon">{icon}</div>
    <div className="empty-title">{title}</div>
    {subtitle && <div style={{ fontSize:13, marginBottom:16 }}>{subtitle}</div>}
    {action}
  </div>
);

// ── US States ─────────────────────────────────────────────────────────────────
export const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

export const CA_PROVINCES = ['AB','BC','MB','NB','NL','NS','NT','NU','ON','PE','QC','SK','YT'];

// ── Countries ─────────────────────────────────────────────────────────────────
export const COUNTRIES = [
  { code:'US', name:'United States', flag:'🇺🇸' },
  { code:'AF', name:'Afghanistan', flag:'🇦🇫' },
  { code:'AL', name:'Albania', flag:'🇦🇱' },
  { code:'DZ', name:'Algeria', flag:'🇩🇿' },
  { code:'AS', name:'American Samoa', flag:'🇦🇸' },
  { code:'AD', name:'Andorra', flag:'🇦🇩' },
  { code:'AO', name:'Angola', flag:'🇦🇴' },
  { code:'AI', name:'Anguilla', flag:'🇦🇮' },
  { code:'AQ', name:'Antarctica', flag:'🇦🇶' },
  { code:'AG', name:'Antigua and Barbuda', flag:'🇦🇬' },
  { code:'AR', name:'Argentina', flag:'🇦🇷' },
  { code:'AM', name:'Armenia', flag:'🇦🇲' },
  { code:'AW', name:'Aruba', flag:'🇦🇼' },
  { code:'AU', name:'Australia', flag:'🇦🇺' },
  { code:'AT', name:'Austria', flag:'🇦🇹' },
  { code:'AZ', name:'Azerbaijan', flag:'🇦🇿' },
  { code:'BS', name:'Bahamas', flag:'🇧🇸' },
  { code:'BH', name:'Bahrain', flag:'🇧🇭' },
  { code:'BD', name:'Bangladesh', flag:'🇧🇩' },
  { code:'BB', name:'Barbados', flag:'🇧🇧' },
  { code:'BY', name:'Belarus', flag:'🇧🇾' },
  { code:'BE', name:'Belgium', flag:'🇧🇪' },
  { code:'BZ', name:'Belize', flag:'🇧🇿' },
  { code:'BJ', name:'Benin', flag:'🇧🇯' },
  { code:'BM', name:'Bermuda', flag:'🇧🇲' },
  { code:'BT', name:'Bhutan', flag:'🇧🇹' },
  { code:'BO', name:'Bolivia', flag:'🇧🇴' },
  { code:'BQ', name:'Bonaire, Sint Eustatius and Saba', flag:'🇧🇶' },
  { code:'BA', name:'Bosnia and Herzegovina', flag:'🇧🇦' },
  { code:'BW', name:'Botswana', flag:'🇧🇼' },
  { code:'BV', name:'Bouvet Island', flag:'🇧🇻' },
  { code:'BR', name:'Brazil', flag:'🇧🇷' },
  { code:'IO', name:'British Indian Ocean Territory', flag:'🇮🇴' },
  { code:'BN', name:'Brunei', flag:'🇧🇳' },
  { code:'BG', name:'Bulgaria', flag:'🇧🇬' },
  { code:'BF', name:'Burkina Faso', flag:'🇧🇫' },
  { code:'BI', name:'Burundi', flag:'🇧🇮' },
  { code:'CV', name:'Cabo Verde', flag:'🇨🇻' },
  { code:'KH', name:'Cambodia', flag:'🇰🇭' },
  { code:'CM', name:'Cameroon', flag:'🇨🇲' },
  { code:'CA', name:'Canada', flag:'🇨🇦' },
  { code:'KY', name:'Cayman Islands', flag:'🇰🇾' },
  { code:'CF', name:'Central African Republic', flag:'🇨🇫' },
  { code:'TD', name:'Chad', flag:'🇹🇩' },
  { code:'CL', name:'Chile', flag:'🇨🇱' },
  { code:'CN', name:'China', flag:'🇨🇳' },
  { code:'CX', name:'Christmas Island', flag:'🇨🇽' },
  { code:'CC', name:'Cocos Islands', flag:'🇨🇨' },
  { code:'CO', name:'Colombia', flag:'🇨🇴' },
  { code:'KM', name:'Comoros', flag:'🇰🇲' },
  { code:'CG', name:'Congo', flag:'🇨🇬' },
  { code:'CD', name:'Congo, DR', flag:'🇨🇩' },
  { code:'CK', name:'Cook Islands', flag:'🇨🇰' },
  { code:'CR', name:'Costa Rica', flag:'🇨🇷' },
  { code:'HR', name:'Croatia', flag:'🇭🇷' },
  { code:'CU', name:'Cuba', flag:'🇨🇺' },
  { code:'CW', name:'Curaçao', flag:'🇨🇼' },
  { code:'CY', name:'Cyprus', flag:'🇨🇾' },
  { code:'CZ', name:'Czech Republic', flag:'🇨🇿' },
  { code:'CI', name:'Côte d\'Ivoire', flag:'🇨🇮' },
  { code:'DK', name:'Denmark', flag:'🇩🇰' },
  { code:'DJ', name:'Djibouti', flag:'🇩🇯' },
  { code:'DM', name:'Dominica', flag:'🇩🇲' },
  { code:'DO', name:'Dominican Republic', flag:'🇩🇴' },
  { code:'EC', name:'Ecuador', flag:'🇪🇨' },
  { code:'EG', name:'Egypt', flag:'🇪🇬' },
  { code:'SV', name:'El Salvador', flag:'🇸🇻' },
  { code:'GQ', name:'Equatorial Guinea', flag:'🇬🇶' },
  { code:'ER', name:'Eritrea', flag:'🇪🇷' },
  { code:'EE', name:'Estonia', flag:'🇪🇪' },
  { code:'SZ', name:'Eswatini', flag:'🇸🇿' },
  { code:'ET', name:'Ethiopia', flag:'🇪🇹' },
  { code:'FK', name:'Falkland Islands', flag:'🇫🇰' },
  { code:'FO', name:'Faroe Islands', flag:'🇫🇴' },
  { code:'FJ', name:'Fiji', flag:'🇫🇯' },
  { code:'FI', name:'Finland', flag:'🇫🇮' },
  { code:'FR', name:'France', flag:'🇫🇷' },
  { code:'GF', name:'French Guiana', flag:'🇬🇫' },
  { code:'PF', name:'French Polynesia', flag:'🇵🇫' },
  { code:'TF', name:'French Southern Territories', flag:'🇹🇫' },
  { code:'GA', name:'Gabon', flag:'🇬🇦' },
  { code:'GM', name:'Gambia', flag:'🇬🇲' },
  { code:'GE', name:'Georgia', flag:'🇬🇪' },
  { code:'DE', name:'Germany', flag:'🇩🇪' },
  { code:'GH', name:'Ghana', flag:'🇬🇭' },
  { code:'GI', name:'Gibraltar', flag:'🇬🇮' },
  { code:'GR', name:'Greece', flag:'🇬🇷' },
  { code:'GL', name:'Greenland', flag:'🇬🇱' },
  { code:'GD', name:'Grenada', flag:'🇬🇩' },
  { code:'GP', name:'Guadeloupe', flag:'🇬🇵' },
  { code:'GU', name:'Guam', flag:'🇬🇺' },
  { code:'GT', name:'Guatemala', flag:'🇬🇹' },
  { code:'GG', name:'Guernsey', flag:'🇬🇬' },
  { code:'GN', name:'Guinea', flag:'🇬🇳' },
  { code:'GW', name:'Guinea-Bissau', flag:'🇬🇼' },
  { code:'GY', name:'Guyana', flag:'🇬🇾' },
  { code:'HT', name:'Haiti', flag:'🇭🇹' },
  { code:'HM', name:'Heard Island and McDonald Islands', flag:'🇭🇲' },
  { code:'VA', name:'Holy See', flag:'🇻🇦' },
  { code:'HN', name:'Honduras', flag:'🇭🇳' },
  { code:'HK', name:'Hong Kong', flag:'🇭🇰' },
  { code:'HU', name:'Hungary', flag:'🇭🇺' },
  { code:'IS', name:'Iceland', flag:'🇮🇸' },
  { code:'IN', name:'India', flag:'🇮🇳' },
  { code:'ID', name:'Indonesia', flag:'🇮🇩' },
  { code:'IR', name:'Iran', flag:'🇮🇷' },
  { code:'IQ', name:'Iraq', flag:'🇮🇶' },
  { code:'IE', name:'Ireland', flag:'🇮🇪' },
  { code:'IM', name:'Isle of Man', flag:'🇮🇲' },
  { code:'IL', name:'Israel', flag:'🇮🇱' },
  { code:'IT', name:'Italy', flag:'🇮🇹' },
  { code:'JM', name:'Jamaica', flag:'🇯🇲' },
  { code:'JP', name:'Japan', flag:'🇯🇵' },
  { code:'JE', name:'Jersey', flag:'🇯🇪' },
  { code:'JO', name:'Jordan', flag:'🇯🇴' },
  { code:'KZ', name:'Kazakhstan', flag:'🇰🇿' },
  { code:'KE', name:'Kenya', flag:'🇰🇪' },
  { code:'KI', name:'Kiribati', flag:'🇰🇮' },
  { code:'KP', name:'Korea, North', flag:'🇰🇵' },
  { code:'KR', name:'Korea, South', flag:'🇰🇷' },
  { code:'XK', name:'Kosovo', flag:'🇽🇰' },
  { code:'KW', name:'Kuwait', flag:'🇰🇼' },
  { code:'KG', name:'Kyrgyzstan', flag:'🇰🇬' },
  { code:'LA', name:'Laos', flag:'🇱🇦' },
  { code:'LV', name:'Latvia', flag:'🇱🇻' },
  { code:'LB', name:'Lebanon', flag:'🇱🇧' },
  { code:'LS', name:'Lesotho', flag:'🇱🇸' },
  { code:'LR', name:'Liberia', flag:'🇱🇷' },
  { code:'LY', name:'Libya', flag:'🇱🇾' },
  { code:'LI', name:'Liechtenstein', flag:'🇱🇮' },
  { code:'LT', name:'Lithuania', flag:'🇱🇹' },
  { code:'LU', name:'Luxembourg', flag:'🇱🇺' },
  { code:'MO', name:'Macao', flag:'🇲🇴' },
  { code:'MG', name:'Madagascar', flag:'🇲🇬' },
  { code:'MW', name:'Malawi', flag:'🇲🇼' },
  { code:'MY', name:'Malaysia', flag:'🇲🇾' },
  { code:'MV', name:'Maldives', flag:'🇲🇻' },
  { code:'ML', name:'Mali', flag:'🇲🇱' },
  { code:'MT', name:'Malta', flag:'🇲🇹' },
  { code:'MH', name:'Marshall Islands', flag:'🇲🇭' },
  { code:'MQ', name:'Martinique', flag:'🇲🇶' },
  { code:'MR', name:'Mauritania', flag:'🇲🇷' },
  { code:'MU', name:'Mauritius', flag:'🇲🇺' },
  { code:'YT', name:'Mayotte', flag:'🇾🇹' },
  { code:'MX', name:'Mexico', flag:'🇲🇽' },
  { code:'FM', name:'Micronesia', flag:'🇫🇲' },
  { code:'MD', name:'Moldova', flag:'🇲🇩' },
  { code:'MC', name:'Monaco', flag:'🇲🇨' },
  { code:'MN', name:'Mongolia', flag:'🇲🇳' },
  { code:'ME', name:'Montenegro', flag:'🇲🇪' },
  { code:'MS', name:'Montserrat', flag:'🇲🇸' },
  { code:'MA', name:'Morocco', flag:'🇲🇦' },
  { code:'MZ', name:'Mozambique', flag:'🇲🇿' },
  { code:'MM', name:'Myanmar', flag:'🇲🇲' },
  { code:'NA', name:'Namibia', flag:'🇳🇦' },
  { code:'NR', name:'Nauru', flag:'🇳🇷' },
  { code:'NP', name:'Nepal', flag:'🇳🇵' },
  { code:'NL', name:'Netherlands', flag:'🇳🇱' },
  { code:'NC', name:'New Caledonia', flag:'🇳🇨' },
  { code:'NZ', name:'New Zealand', flag:'🇳🇿' },
  { code:'NI', name:'Nicaragua', flag:'🇳🇮' },
  { code:'NE', name:'Niger', flag:'🇳🇪' },
  { code:'NG', name:'Nigeria', flag:'🇳🇬' },
  { code:'NU', name:'Niue', flag:'🇳🇺' },
  { code:'NF', name:'Norfolk Island', flag:'🇳🇫' },
  { code:'MK', name:'North Macedonia', flag:'🇲🇰' },
  { code:'MP', name:'Northern Mariana Islands', flag:'🇲🇵' },
  { code:'NO', name:'Norway', flag:'🇳🇴' },
  { code:'OM', name:'Oman', flag:'🇴🇲' },
  { code:'PK', name:'Pakistan', flag:'🇵🇰' },
  { code:'PW', name:'Palau', flag:'🇵🇼' },
  { code:'PS', name:'Palestine', flag:'🇵🇸' },
  { code:'PA', name:'Panama', flag:'🇵🇦' },
  { code:'PG', name:'Papua New Guinea', flag:'🇵🇬' },
  { code:'PY', name:'Paraguay', flag:'🇵🇾' },
  { code:'PE', name:'Peru', flag:'🇵🇪' },
  { code:'PH', name:'Philippines', flag:'🇵🇭' },
  { code:'PN', name:'Pitcairn', flag:'🇵🇳' },
  { code:'PL', name:'Poland', flag:'🇵🇱' },
  { code:'PT', name:'Portugal', flag:'🇵🇹' },
  { code:'PR', name:'Puerto Rico', flag:'🇵🇷' },
  { code:'QA', name:'Qatar', flag:'🇶🇦' },
  { code:'RO', name:'Romania', flag:'🇷🇴' },
  { code:'RU', name:'Russia', flag:'🇷🇺' },
  { code:'RW', name:'Rwanda', flag:'🇷🇼' },
  { code:'RE', name:'Réunion', flag:'🇷🇪' },
  { code:'BL', name:'Saint Barthélemy', flag:'🇧🇱' },
  { code:'SH', name:'Saint Helena', flag:'🇸🇭' },
  { code:'KN', name:'Saint Kitts and Nevis', flag:'🇰🇳' },
  { code:'LC', name:'Saint Lucia', flag:'🇱🇨' },
  { code:'MF', name:'Saint Martin (French)', flag:'🇲🇫' },
  { code:'PM', name:'Saint Pierre and Miquelon', flag:'🇵🇲' },
  { code:'VC', name:'Saint Vincent and the Grenadines', flag:'🇻🇨' },
  { code:'WS', name:'Samoa', flag:'🇼🇸' },
  { code:'SM', name:'San Marino', flag:'🇸🇲' },
  { code:'ST', name:'Sao Tome and Principe', flag:'🇸🇹' },
  { code:'SA', name:'Saudi Arabia', flag:'🇸🇦' },
  { code:'SN', name:'Senegal', flag:'🇸🇳' },
  { code:'RS', name:'Serbia', flag:'🇷🇸' },
  { code:'SC', name:'Seychelles', flag:'🇸🇨' },
  { code:'SL', name:'Sierra Leone', flag:'🇸🇱' },
  { code:'SG', name:'Singapore', flag:'🇸🇬' },
  { code:'SX', name:'Sint Maarten (Dutch)', flag:'🇸🇽' },
  { code:'SK', name:'Slovakia', flag:'🇸🇰' },
  { code:'SI', name:'Slovenia', flag:'🇸🇮' },
  { code:'SB', name:'Solomon Islands', flag:'🇸🇧' },
  { code:'SO', name:'Somalia', flag:'🇸🇴' },
  { code:'ZA', name:'South Africa', flag:'🇿🇦' },
  { code:'GS', name:'South Georgia and South Sandwich Islands', flag:'🇬🇸' },
  { code:'SS', name:'South Sudan', flag:'🇸🇸' },
  { code:'ES', name:'Spain', flag:'🇪🇸' },
  { code:'LK', name:'Sri Lanka', flag:'🇱🇰' },
  { code:'SD', name:'Sudan', flag:'🇸🇩' },
  { code:'SR', name:'Suriname', flag:'🇸🇷' },
  { code:'SJ', name:'Svalbard and Jan Mayen', flag:'🇸🇯' },
  { code:'SE', name:'Sweden', flag:'🇸🇪' },
  { code:'CH', name:'Switzerland', flag:'🇨🇭' },
  { code:'SY', name:'Syria', flag:'🇸🇾' },
  { code:'TW', name:'Taiwan', flag:'🇹🇼' },
  { code:'TJ', name:'Tajikistan', flag:'🇹🇯' },
  { code:'TZ', name:'Tanzania', flag:'🇹🇿' },
  { code:'TH', name:'Thailand', flag:'🇹🇭' },
  { code:'TL', name:'Timor-Leste', flag:'🇹🇱' },
  { code:'TG', name:'Togo', flag:'🇹🇬' },
  { code:'TK', name:'Tokelau', flag:'🇹🇰' },
  { code:'TO', name:'Tonga', flag:'🇹🇴' },
  { code:'TT', name:'Trinidad and Tobago', flag:'🇹🇹' },
  { code:'TN', name:'Tunisia', flag:'🇹🇳' },
  { code:'TR', name:'Turkey', flag:'🇹🇷' },
  { code:'TM', name:'Turkmenistan', flag:'🇹🇲' },
  { code:'TC', name:'Turks and Caicos Islands', flag:'🇹🇨' },
  { code:'TV', name:'Tuvalu', flag:'🇹🇻' },
  { code:'UM', name:'US Minor Outlying Islands', flag:'🇺🇲' },
  { code:'VI', name:'US Virgin Islands', flag:'🇻🇮' },
  { code:'UG', name:'Uganda', flag:'🇺🇬' },
  { code:'UA', name:'Ukraine', flag:'🇺🇦' },
  { code:'AE', name:'United Arab Emirates', flag:'🇦🇪' },
  { code:'GB', name:'United Kingdom', flag:'🇬🇧' },
  { code:'UY', name:'Uruguay', flag:'🇺🇾' },
  { code:'UZ', name:'Uzbekistan', flag:'🇺🇿' },
  { code:'VU', name:'Vanuatu', flag:'🇻🇺' },
  { code:'VE', name:'Venezuela', flag:'🇻🇪' },
  { code:'VN', name:'Vietnam', flag:'🇻🇳' },
  { code:'VG', name:'Virgin Islands, British', flag:'🇻🇬' },
  { code:'WF', name:'Wallis and Futuna', flag:'🇼🇫' },
  { code:'EH', name:'Western Sahara', flag:'🇪🇭' },
  { code:'YE', name:'Yemen', flag:'🇾🇪' },
  { code:'ZM', name:'Zambia', flag:'🇿🇲' },
  { code:'ZW', name:'Zimbabwe', flag:'🇿🇼' },
  { code:'AX', name:'Åland Islands', flag:'🇦🇽' },
];

// ── Country Typeahead ─────────────────────────────────────────────────────────
export function CountrySelect({ value, onChange }) {
  const country   = COUNTRIES.find(c => c.code === value) || COUNTRIES[0];
  const [query,   setQuery]   = useState('');
  const [open,    setOpen]    = useState(false);
  const [focused, setFocused] = useState(0);
  const inputRef  = useRef(null);
  const listRef   = useRef(null);

  const filtered = query.trim()
    ? COUNTRIES.filter(c => c.name.toLowerCase().includes(query.toLowerCase()) || c.code.toLowerCase().includes(query.toLowerCase()))
    : COUNTRIES;

  useEffect(() => {
    if (!open) { setQuery(''); setFocused(0); }
  }, [open]);

  useEffect(() => {
    const handler = (e) => { if (!e.target.closest('.country-select')) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const select = (c) => { onChange(c.code); setOpen(false); };

  const handleKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setFocused(f => Math.min(f+1, filtered.length-1)); }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setFocused(f => Math.max(f-1, 0)); }
    if (e.key === 'Enter')     { e.preventDefault(); if (filtered[focused]) select(filtered[focused]); }
    if (e.key === 'Escape')    { setOpen(false); }
  };

  return (
    <div className="country-select" style={{ position:'relative' }}>
      <div
        className="input"
        style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', userSelect:'none' }}
        onClick={() => { setOpen(o => !o); setTimeout(() => inputRef.current?.focus(), 50); }}
      >
        <span style={{ fontSize:18 }}>{country.flag}</span>
        <span style={{ flex:1 }}>{country.name}</span>
        <span style={{ color:'var(--text3)', fontSize:11 }}>▼</span>
      </div>
      {open && (
        <div style={{ position:'absolute', top:'100%', left:0, right:0, zIndex:200, background:'var(--surface)', border:'1px solid var(--brand)', borderRadius:'var(--radius)', boxShadow:'var(--shadow-md)', marginTop:2 }}>
          <div style={{ padding:'6px 8px', borderBottom:'1px solid var(--border)' }}>
            <input
              ref={inputRef}
              className="input"
              style={{ padding:'6px 10px', fontSize:13 }}
              placeholder="Search country..."
              value={query}
              onChange={e => { setQuery(e.target.value); setFocused(0); }}
              onKeyDown={handleKey}
              autoFocus
            />
          </div>
          <div ref={listRef} style={{ maxHeight:200, overflowY:'auto' }}>
            {filtered.length === 0
              ? <div style={{ padding:'10px 12px', fontSize:13, color:'var(--text3)' }}>No countries found</div>
              : filtered.map((c, i) => (
                <div key={c.code}
                  onMouseDown={() => select(c)}
                  style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 12px', cursor:'pointer',
                    background: i === focused ? 'var(--brand-light)' : (c.code === value ? 'var(--bg)' : 'transparent'),
                    fontWeight: c.code === value ? 600 : 400, fontSize:13
                  }}>
                  <span style={{ fontSize:18, flexShrink:0 }}>{c.flag}</span>
                  <span style={{ flex:1 }}>{c.name}</span>
                  <span style={{ fontSize:11, color:'var(--text3)' }}>{c.code}</span>
                  {c.code === value && <span style={{ color:'var(--brand)', fontSize:12 }}>✓</span>}
                </div>
              ))
            }
          </div>
        </div>
      )}
    </div>
  );
}

// ── Formatters ────────────────────────────────────────────────────────────────
export const fmtDate  = d => new Date(d).toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric' });
export const fmtMoney = n => `$${Number(n||0).toFixed(2)}`;
export const fmtSize  = b => { if (!b) return ''; if (b < 1048576) return `${(b/1024).toFixed(0)} KB`; return `${(b/1048576).toFixed(1)} MB`; };
